import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  LuUsers, 
  LuUserCheck, 
  LuBriefcase, 
  LuSearch, 
  LuMail, 
  LuPhone, 
  LuCalendar, 
  LuClock
} from 'react-icons/lu';
import BASE_URL from '../../url';
import '../CrmStyles.css';

const CompanyDeployments = ({ companyId }) => {
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    const fetchDeployments = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Attempt primary CRM deployment endpoint
        try {
          const res = await axios.get(`${BASE_URL}/api/crm/companies/${companyId}/deployments`, { headers });
          if (res.data.success) {
            setDeployments(res.data.data?.deployments || []);
            setLoading(false);
            return;
          }
        } catch (crmErr) {
          console.warn('Primary CRM deployments route unavailable, trying fallback:', crmErr.message);
        }

        const fallbackRes = await axios.get(`${BASE_URL}/api/companies/deployments-report?clientId=${companyId}`, { headers });
        if (fallbackRes.data.success) {
          setDeployments(fallbackRes.data.data || []);
        } else {
          setDeployments([]);
        }
      } catch (err) {
        console.error('Error fetching company deployments:', err);
        setError('Unable to load deployment records at this time.');
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchDeployments();
    }
  }, [companyId]);

  // Statistics
  const totalCount = deployments.length;
  const activeCount = deployments.filter(d => (d.StatusCode || '').toUpperCase() === 'ACTIVE').length;
  const completedCount = deployments.filter(d => ['COMPLETED', 'TERMINATED', 'INACTIVE'].includes((d.StatusCode || '').toUpperCase())).length;

  // Filtered Deployments
  const filtered = deployments.filter(d => {
    const searchLower = searchTerm.toLowerCase();
    const candName = (d.CandidateName || `${d.FirstName || ''} ${d.LastName || ''}`).toLowerCase();
    const title = (d.PlacedJobTitle || d.JobTitle || '').toLowerCase();
    const email = (d.Email || '').toLowerCase();
    const code = (d.EmployeeCode || d.JobCode || '').toLowerCase();

    const matchesSearch = !searchTerm || 
      candName.includes(searchLower) || 
      title.includes(searchLower) || 
      email.includes(searchLower) || 
      code.includes(searchLower);

    const matchesStatus = statusFilter === 'ALL' || 
      (d.StatusCode || '').toUpperCase() === statusFilter.toUpperCase();

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadgeClass = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'ACTIVE') return 'crm-badge-active';
    if (s === 'PENDING') return 'crm-badge-prospect';
    if (s === 'COMPLETED') return 'crm-badge-role';
    return 'crm-badge-inactive';
  };

  return (
    <div>
      {/* Metric Stat Cards */}
      <div className="crm-stats-grid">
        <div className="crm-stat-box">
          <div className="crm-stat-icon-wrapper" style={{ background: '#eff6ff', color: '#1d4ed8' }}>
            <LuUsers size={22} />
          </div>
          <div className="crm-stat-info">
            <h4>{totalCount}</h4>
            <span>Total Deployed</span>
          </div>
        </div>

        <div className="crm-stat-box">
          <div className="crm-stat-icon-wrapper" style={{ background: 'rgba(59, 91, 253, 0.12)', color: '#3b5bfd' }}>
            <LuUserCheck size={22} />
          </div>
          <div className="crm-stat-info">
            <h4>{activeCount}</h4>
            <span>Active Deployments</span>
          </div>
        </div>

        <div className="crm-stat-box">
          <div className="crm-stat-icon-wrapper" style={{ background: '#f3f4f6', color: '#4b5563' }}>
            <LuClock size={22} />
          </div>
          <div className="crm-stat-info">
            <h4>{completedCount}</h4>
            <span>Past / Inactive</span>
          </div>
        </div>
      </div>

      {/* Deployments Card */}
      <div className="crm-card">
        <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LuUsers size={18} style={{ color: '#3b5bfd' }} />
            Deployed Candidates ({filtered.length})
          </h3>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Search Box */}
            <div style={{ position: 'relative', width: '240px' }}>
              <LuSearch size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                className="crm-input"
                placeholder="Search candidates, roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '32px', height: '36px', fontSize: '13px' }}
              />
            </div>

            {/* Status Filter */}
            <select
              className="crm-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: '130px', height: '36px', fontSize: '13px' }}
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="COMPLETED">Completed</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
        </div>

        <div className="crm-card-body" style={{ padding: 0 }}>
          {error && (
            <div style={{ padding: '16px', color: '#b91c1c', background: '#fef2f2', borderBottom: '1px solid #fecaca', fontSize: '14px' }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ padding: '36px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
              Loading candidate deployments...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
              <LuUsers size={40} style={{ color: '#d1d5db', marginBottom: '12px' }} />
              <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#374151' }}>No candidates deployed</h4>
              <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                {searchTerm || statusFilter !== 'ALL' 
                  ? 'No deployed candidates match your search filters.' 
                  : 'There are currently no candidates deployed with or placed through this company.'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="crm-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Role / Job Title</th>
                    <th>Relationship</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Contact Info</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((dep, idx) => {
                    const name = dep.CandidateName || `${dep.FirstName || ''} ${dep.LastName || ''}`.trim() || `Consultant #${dep.DeploymentId || idx + 1}`;
                    const initials = name.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
                    const role = dep.PlacedJobTitle || dep.JobTitle || 'Contractor / Consultant';
                    const relation = dep.RelationshipRole || (dep.ClientCompanyId === companyId ? 'Direct Client' : 'Placement Chain');

                    return (
                      <tr key={dep.DeploymentId || idx}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '34px',
                              height: '34px',
                              borderRadius: '50%',
                              backgroundColor: 'rgba(1, 157, 136, 0.1)',
                              color: '#3b5bfd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '600',
                              fontSize: '12px',
                              flexShrink: 0
                            }}>
                              {initials || 'C'}
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: '#111827' }}>
                                {name}
                              </div>
                              {dep.EmployeeCode && (
                                <span style={{ fontSize: '11px', color: '#6b7280', fontFamily: 'monospace' }}>
                                  {dep.EmployeeCode}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <LuBriefcase size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />
                            <span style={{ fontWeight: '500' }}>{role}</span>
                          </div>
                          {dep.JobCode && (
                            <span style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginTop: '2px' }}>
                              Req #{dep.JobCode}
                            </span>
                          )}
                        </td>

                        <td>
                          <span className="crm-badge crm-badge-role" style={{ fontSize: '11px' }}>
                            {relation}
                          </span>
                        </td>

                        <td>
                          <span className={`crm-badge ${getStatusBadgeClass(dep.StatusCode)}`}>
                            {(dep.StatusCode || 'ACTIVE').toUpperCase()}
                          </span>
                        </td>

                        <td>
                          <div style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <LuCalendar size={13} style={{ color: '#9ca3af', flexShrink: 0 }} />
                            <span>
                              {formatDate(dep.StartDate)}
                              {dep.EndDate ? ` — ${formatDate(dep.EndDate)}` : ' — Present'}
                            </span>
                          </div>
                        </td>

                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '13px' }}>
                            {dep.Email && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <LuMail size={13} style={{ color: '#9ca3af', flexShrink: 0 }} />
                                <span style={{ color: '#374151' }}>{dep.Email}</span>
                              </div>
                            )}
                            {dep.Phone && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <LuPhone size={13} style={{ color: '#9ca3af', flexShrink: 0 }} />
                                <span style={{ color: '#6b7280' }}>{dep.Phone}</span>
                              </div>
                            )}
                            {!dep.Email && !dep.Phone && (
                              <span style={{ color: '#9ca3af' }}>N/A</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyDeployments;
